import React, { useMemo, useState } from "react";
import { COUNTY_RIGHTS_LINKS, RIGHTS_DATA, RIGHTS_DISCLAIMER, normalizeRightsState } from "../../utils/rightsData";
import { normalizeLanguage } from "../../utils/i18n";

const ROLE_ORDER = ["tenant", "landlord", "property_manager"];

const roleLabels = {
  tenant: "Tenant Rights",
  landlord: "Landlord Rights",
  property_manager: "Property Manager Responsibilities",
};

const roleLabelsEs = {
  tenant: "Derechos de inquilinos",
  landlord: "Derechos de propietarios",
  property_manager: "Responsabilidades de administradores",
};

const summaryEs = {
  tenant:
    "Los inquilinos deben entender protecciones de vivienda justa, condiciones habitables, depositos, avisos y el proceso local antes de que surjan problemas.",
  landlord:
    "Los propietarios deben entender vivienda justa, evaluacion legal, mantenimiento, cumplimiento del contrato, avisos y procesos judiciales.",
  property_manager:
    "Los administradores deben entender vivienda justa, respuesta a mantenimiento, licencias cuando apliquen, registros y responsabilidades con propietarios e inquilinos.",
};

export default function RightsResources({
  stateCode = "",
  defaultRole = "",
  location = {},
  title = "Know Your Rights",
  compact = true,
  language = "en",
}) {
  const [expanded, setExpanded] = useState({});
  const [localRole, setLocalRole] = useState("");
  const normalizedLanguage = normalizeLanguage(language);
  const resolvedState = normalizeRightsState(stateCode);
  const stateData = RIGHTS_DATA[resolvedState] || RIGHTS_DATA.NJ;
  const countyKey = String(location?.county || "").trim().toLowerCase();
  const cityLabel = [location?.city || location?.town || "", location?.county ? `${location.county} County` : "", resolvedState]
    .filter(Boolean)
    .join(", ");

  const orderedRoles = useMemo(() => {
    const role = ROLE_ORDER.includes(defaultRole) ? defaultRole : localRole;
    return ROLE_ORDER.includes(role) ? [role] : [];
  }, [defaultRole, localRole]);

  const hasRole = orderedRoles.length > 0;
  const resolvedTitle =
    normalizedLanguage === "es"
      ? "Conozca sus derechos"
      : title === "Know Your Rights"
      ? "Know Your Rights"
      : title;

  return (
    <section style={styles.shell}>
      <div style={styles.headerRow}>
        <div>
          <div style={styles.eyebrow}>
            {normalizedLanguage === "es" ? "Ayuda de RentDuo" : "RentDuo Help"}
          </div>
          <h3 style={styles.title}>{resolvedTitle}</h3>
        </div>
        <span style={styles.statePill}>{resolvedState}</span>
      </div>
      {cityLabel ? (
        <p style={styles.locationLine}>
          {normalizedLanguage === "es" ? "Ubicacion usada para recursos:" : "Location used for resources:"} {cityLabel}
        </p>
      ) : null}
      <p style={styles.intro}>
        {normalizedLanguage === "es"
          ? "RentDuo ayuda tanto a inquilinos como a proveedores de vivienda a entender derechos y responsabilidades."
          : "RentDuo helps both renters and housing providers understand rights and responsibilities."}
      </p>

      {!hasRole ? (
        <div style={styles.rolePrompt}>
          <p style={styles.summary}>
            {normalizedLanguage === "es"
              ? "Elija un rol para ver derechos y responsabilidades."
              : "Choose a role to see rights and responsibilities."}
          </p>
          <div style={styles.roleButtonRow}>
            {ROLE_ORDER.map((roleKey) => (
              <button
                key={roleKey}
                type="button"
                style={styles.roleChoiceButton}
                onClick={() => setLocalRole(roleKey)}
              >
                {normalizedLanguage === "es" ? roleLabelsEs[roleKey] : roleLabels[roleKey]}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {hasRole ? (
        <div style={styles.cardGrid}>
          {orderedRoles.map((roleKey) => {
            const item = stateData[roleKey] || RIGHTS_DATA.US[roleKey];
            const isOpen = Boolean(expanded[roleKey]);
            const allLinks = [...(item.links || []), ...(COUNTY_RIGHTS_LINKS[resolvedState]?.[countyKey] || [])];
            const links = compact && !isOpen ? allLinks.slice(0, 1) : allLinks;

            return (
              <article key={roleKey} style={styles.card}>
                <button
                  type="button"
                  style={styles.cardButton}
                  onClick={() => setExpanded((prev) => ({ ...prev, [roleKey]: !prev[roleKey] }))}
                >
                  <span>{normalizedLanguage === "es" ? roleLabelsEs[roleKey] : roleLabels[roleKey]}</span>
                  <span style={styles.expandIcon}>
                    {isOpen
                      ? normalizedLanguage === "es"
                        ? "Ocultar"
                        : "Hide"
                      : normalizedLanguage === "es"
                      ? "Mostrar"
                      : "Show"}
                  </span>
                </button>
                <p style={styles.summary}>
                  {normalizedLanguage === "es" ? summaryEs[roleKey] : item.summary}
                </p>

                {isOpen ? (
                  <div style={styles.linkList}>
                    {links.map((link) => (
                      <a key={link.url} href={link.url} target="_blank" rel="noreferrer" style={styles.linkCard}>
                        <span style={styles.linkTitle}>{link.title}</span>
                        <span style={styles.linkType}>{link.type}</span>
                        <span style={styles.linkDescription}>
                          {normalizedLanguage === "es"
                            ? "Recurso oficial para informacion de vivienda, derechos y responsabilidades."
                            : link.description}
                        </span>
                      </a>
                    ))}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : null}

      <p style={styles.disclaimer}>
        {normalizedLanguage === "es"
          ? "Esta información es solo educativa y no constituye asesoramiento legal."
          : RIGHTS_DISCLAIMER}
      </p>
    </section>
  );
}

const styles = {
  shell: {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    overflowX: "hidden",
    borderRadius: 18,
    border: "1px solid rgba(191,219,254,0.95)",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    padding: 14,
    margin: "14px 0",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  eyebrow: {
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    color: "#0f6fff",
  },
  title: {
    margin: "4px 0 0",
    fontSize: 18,
    lineHeight: 1.15,
    color: "#0f172a",
  },
  statePill: {
    borderRadius: 999,
    background: "#ecfdf5",
    color: "#047857",
    fontSize: 12,
    fontWeight: 900,
    padding: "7px 10px",
  },
  intro: {
    margin: "10px 0 12px",
    color: "#475569",
    fontSize: 14,
    lineHeight: 1.5,
  },
  locationLine: {
    margin: "8px 0 0",
    color: "#0f766e",
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.45,
  },
  rolePrompt: {
    border: "1px solid rgba(226,232,240,0.95)",
    borderRadius: 14,
    background: "#ffffff",
    padding: 12,
    boxSizing: "border-box",
  },
  roleButtonRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  roleChoiceButton: {
    border: "1px solid rgba(59,130,246,0.24)",
    borderRadius: 999,
    background: "#eff6ff",
    color: "#1d4ed8",
    fontSize: 12,
    fontWeight: 900,
    padding: "8px 10px",
    cursor: "pointer",
    boxSizing: "border-box",
    whiteSpace: "normal",
  },
  cardGrid: {
    display: "grid",
    gap: 10,
  },
  card: {
    border: "1px solid rgba(226,232,240,0.95)",
    borderRadius: 14,
    background: "#ffffff",
    padding: 12,
    boxSizing: "border-box",
    minWidth: 0,
  },
  cardButton: {
    width: "100%",
    border: 0,
    background: "transparent",
    padding: 0,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    color: "#0f172a",
    fontWeight: 900,
    fontSize: 15,
    textAlign: "left",
    cursor: "pointer",
  },
  expandIcon: {
    color: "#0f6fff",
    fontSize: 12,
    fontWeight: 900,
    flexShrink: 0,
  },
  summary: {
    margin: "8px 0 0",
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.5,
  },
  linkList: {
    display: "grid",
    gap: 8,
    marginTop: 10,
  },
  linkCard: {
    display: "grid",
    gap: 4,
    textDecoration: "none",
    borderRadius: 12,
    border: "1px solid rgba(219,234,254,0.95)",
    background: "#eff6ff",
    padding: 10,
    minWidth: 0,
  },
  linkTitle: {
    color: "#1d4ed8",
    fontWeight: 900,
    fontSize: 13,
  },
  linkType: {
    color: "#047857",
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
  },
  linkDescription: {
    color: "#475569",
    fontSize: 12,
    lineHeight: 1.45,
  },
  disclaimer: {
    margin: "12px 0 0",
    color: "#64748b",
    fontSize: 12,
    lineHeight: 1.45,
  },
};
